/**
 * 74.19 (DECISIONS 427 (3), 433, 435; the sheet: design_sheets/erosion_carving.html): **erosion carving**. A planet
 * carved by its own drainage at generation: valleys deepened along the drainage chains, the ridges between them left
 * standing (so sharper), fans where a steep channel meets gentle ground, flat floors where a big chain's grade is low.
 * Behind `MatchSetup.erosion` ('off' | 'light' | 'strong'; off is the ground before it, bit for bit), on the action clock only;
 * light by default there at a person's scale since DECISIONS 441 (v224).
 *
 * Where it runs (core/mapgen.ts generateMap, before anything stands on the ground):
 * - **Landforms off:** `withErosion`, on the generated planet, after `generatePlanetGeometry`.
 * - **Landforms on:** inside the landform pass (core/landforms.ts landformsOf, step 5½, lane C's hook): after their uplift,
 *   valleys and thermal pass, before their features. It runs on the ground as it stands there (the base field and the
 *   landforms' layer together), and its change is kept apart from their layer. Both the mesh and step 6's radii take it,
 *   so the cliffs, the passes and their records' levels are found on the carved ground and equal it to the bit. It holds
 *   what the pass must keep: the caprock rims and cirque headwalls (`fixed`) and the scree ground.
 * - **Never on an airless world:** no water, no cut (as the landforms' own stream power).
 *
 * The pass, on the planet's weld (core/planetRoutes.ts), with the world sheet's drainage (core/worldSheet.ts drainageOn: the
 * basins and spills with the jitter's pits filled, each vertex's way down, the flow, the chains) on the heights given:
 * 1. **The basins kept.** A basin's floor (under its spill), its sink and its spill vertex are not cut, and the held
 *    vertices are not touched. Every basin keeps its depth, so the lakes, the playas and 391's shore rule stand.
 * 2. **The cut, by flow**: a vertex the flow passes, from the first chain's flow (`WORLD_SHEET.flowFrom`) up, is cut
 *    `depthR` × R × min(1, (A / `flowRef`)^m), with A the flow and m ½ as stream power's. A creek's head nicks the
 *    ground, while a big river's lower reaches take the full depth.
 * 3. **The walls**: the cut spreads out from the chains, falling `wallGrade` (rise over run) an edge, as a V-shaped valley.
 *    The ridges away from a chain are not touched, so they stand higher over the valleys beside them.
 * 4. **Floodplains**: where a chain of order ≥ `plainOrder` runs at a grade under `plainGrade`, its banks are drawn down
 *    `plainPull` of the way to its floor.
 * 5. **Fans**: where a chain's grade falls from past `fanFrom` to under `fanTo`, a cone `fanR` × the cut above it is laid at
 *    the break and its ring, never above the chain's vertex upstream.
 * 6. **The basins' levels**: no vertex of a basin that stood over its spill is left under it.
 * 7. **Still downhill**: from the sinks up, each vertex not held is kept above its way down (no new pit).
 * Only what the pass changed moves: the jitter's pits, filled for the drainage, keep their own heights elsewhere.
 * (The sheet's first prototype ran stream power's implicit step with no uplift. On a made world it flattened every slope
 * toward its outlet and refilled the chains; carving by the sheet's flow is what cuts valleys into a world already made.)
 *
 * Pure and deterministic: dmath, fixed orders, ties by index. Cost: a few passes over the weld (3–11 ms a megaplanet, the
 * sheet's measure), with the drainage's (the world sheet's steps 1–3, shared with it when the heights are the generated ones).
 */
import type { PlanetState } from '../types';
import { planetRouteGraph, type PlanetRouteGraph } from './planetRoutes';
import { drainageOn, worldDrainageOf, WORLD_SHEET, type Drainage } from './worldSheet';
import { groundKey } from './spinAxis';
import { airOf } from './air';
import { dpow } from './dmath';
import { MAX_TERRAIN_HEIGHT_FRACTION, MAX_CRATER_DEPTH_FRACTION } from '../constants/physics';

export type ErosionDial = 'off' | 'light' | 'strong';

export interface ErosionDials {
  /** the full cut (share of R), the flow it is reached at, the flow's exponent */
  depthR: number; flowRef: number; m: number;
  /** the walls' grade (rise over run) */
  wallGrade: number;
  /** floodplains: the chain order (0–1, the sheet's), the grade under which a chain's banks are drawn down, how far */
  plainOrder: number; plainGrade: number; plainPull: number;
  /** fans: the grade above the break, the grade below it, the cone's height as a share of the cut above */
  fanFrom: number; fanTo: number; fanR: number;
}

/** The dials (DECISIONS 435: strong the pick, light the fallback). */
export const EROSION: Record<Exclude<ErosionDial, 'off'>, ErosionDials> = {
  light: { depthR: 0.012, flowRef: 400, m: 0.5, wallGrade: 0.35, plainOrder: 0.6, plainGrade: 0.06, plainPull: 0.5, fanFrom: 0.2, fanTo: 0.08, fanR: 0.35 },
  strong: { depthR: 0.03, flowRef: 300, m: 0.5, wallGrade: 0.6, plainOrder: 0.45, plainGrade: 0.1, plainPull: 0.8, fanFrom: 0.2, fanTo: 0.1, fanR: 0.45 },
};

/** Below this air density a world is not carved (the landforms' own rule for their stream power). */
export const EROSION_AIR = 0.05;

export interface ErosionCut {
  /** per weld vertex: the change in world units (negative cut, positive laid; 0 where the pass changed nothing) */
  delta: Float64Array;
  /** per weld vertex: 1 a chain cut, 2 a wall, 3 a floodplain, 4 a fan; 0 untouched */
  what: Uint8Array;
  /** the drainage it followed */
  drainage: Drainage;
}

/** Whether a dial carves this world at all (not off, and the world has air). */
export function erodes(planet: Pick<PlanetState, 'archetype' | 'palette' | 'radius' | 'heightmap'>, dial: ErosionDial | undefined): dial is Exclude<ErosionDial, 'off'> {
  return dial !== undefined && dial !== 'off' && airOf(planet).density > EROSION_AIR;
}

/**
 * The carve of `radius` (one height a weld vertex, from the planet's centre) at a dial; `hold` marks vertices it must not
 * move; `drainage` the drainage of exactly these heights when the caller has it. Null when the dial is off or the world
 * is airless. Pure.
 */
export function erosionCut(planet: PlanetState, graph: PlanetRouteGraph, radius: Float64Array, dial: ErosionDial | undefined, hold?: Uint8Array, drainage?: Drainage): ErosionCut | null {
  if (!erodes(planet, dial)) return null;
  const D = EROSION[dial], V = graph.count, d = graph.dirs, R = planet.radius;
  const dr = drainage ?? drainageOn(planet, graph, radius);
  const nb = (v: number) => graph.nbr.subarray(graph.start[v], graph.start[v + 1]);
  const runOf = (a: number, b: number) => { const dx = d[3 * a] - d[3 * b], dy = d[3 * a + 1] - d[3 * b + 1], dz = d[3 * a + 2] - d[3 * b + 2]; return Math.sqrt(dx * dx + dy * dy + dz * dz) * R; };
  const start = dr.filled, H = Float64Array.from(start), what = new Uint8Array(V);
  // 1. the basins kept, and the held
  const keep = new Uint8Array(V);
  if (hold) for (let v = 0; v < V; v++) if (hold[v]) keep[v] = 1;
  for (const b of dr.basins) {
    keep[b.sink] = 1;
    if (!Number.isFinite(b.spillRadius)) continue;
    for (const v of b.vertices) if (radius[v] <= b.spillRadius) keep[v] = 1;
    if (b.spillVertex >= 0) keep[b.spillVertex] = 1;
  }
  // 2. the cut by flow (world units, positive down)
  const cut = new Float64Array(V);
  for (let v = 0; v < V; v++) {
    if (keep[v] || dr.flow[v] < WORLD_SHEET.flowFrom) continue;
    cut[v] = D.depthR * R * Math.min(1, dpow(dr.flow[v] / D.flowRef, D.m));
    what[v] = 1;
  }
  // 3. the walls: relaxed out from the chains, an edge at a time in index order, as many edges as the full cut needs
  const wallHops = Math.max(1, Math.ceil(D.depthR * R / (D.wallGrade * runOf(0, graph.nbr[graph.start[0]]))) + 1);
  for (let pass = 0; pass < wallHops; pass++) {
    for (let v = 0; v < V; v++) {
      if (cut[v] <= 0) continue;
      for (const n of nb(v)) {
        if (keep[n]) continue;
        const c = cut[v] - D.wallGrade * runOf(v, n);
        if (c > cut[n]) { cut[n] = c; if (!what[n]) what[n] = 2; }
      }
    }
  }
  for (let v = 0; v < V; v++) H[v] = start[v] - cut[v];
  // 4. floodplains: a big chain's low-grade reach takes a flat floor
  for (const c of dr.chains) {
    if (c.order < D.plainOrder) continue;
    for (let k = 0; k + 1 < c.vertices.length; k++) {
      const v = c.vertices[k], w = c.vertices[k + 1];
      if (Math.abs(H[v] - H[w]) / Math.max(1e-9, runOf(v, w)) >= D.plainGrade) continue;
      for (const n of nb(v)) {
        if (keep[n] || H[n] <= H[v]) continue;
        H[n] -= (H[n] - H[v]) * D.plainPull; what[n] = 3;
      }
    }
  }
  // 5. fans at the grade's breaks
  for (const c of dr.chains) {
    for (let k = 1; k + 1 < c.vertices.length; k++) {
      const u = c.vertices[k - 1], v = c.vertices[k], w = c.vertices[k + 1];
      if (keep[v]) continue;
      const above = (H[u] - H[v]) / Math.max(1e-9, runOf(u, v)), below = (H[v] - H[w]) / Math.max(1e-9, runOf(v, w));
      if (above < D.fanFrom || below > D.fanTo) continue;
      const cone = D.fanR * cut[u];
      const lay = (x: number, h: number) => { if (keep[x] || h <= 0) return; const top = Math.min(H[x] + h, H[u] - 1e-6 * R); if (top > H[x]) { H[x] = top; what[x] = 4; } };
      lay(v, cone);
      for (const n of nb(v)) if (n !== u) lay(n, cone * 0.5);
    }
  }
  // 6. the basins' levels: no rim left under its spill
  for (const b of dr.basins) if (Number.isFinite(b.spillRadius)) for (const v of b.vertices) if (!(hold && hold[v]) && radius[v] > b.spillRadius && H[v] < b.spillRadius) H[v] = b.spillRadius + 1e-6 * R;
  // 7. still downhill: from the sinks up, each vertex not held kept above its way down
  const bySink = new Int32Array(V);
  for (let v = 0; v < V; v++) bySink[v] = v;
  bySink.sort((a, b) => (start[a] - start[b]) || (a - b));
  for (const v of bySink) { const r = dr.downhill[v]; if (r >= 0 && !(hold && hold[v]) && H[v] <= H[r]) H[v] = H[r] + 1e-6 * R; }
  // only what the pass changed moves (the pits filled for the drainage keep their own heights)
  const delta = new Float64Array(V);
  for (let v = 0; v < V; v++) if (Math.abs(H[v] - start[v]) > 1e-9 * R) delta[v] = H[v] - radius[v];
  return { delta, what, drainage: dr };
}

/**
 * The landforms-off path: the generated planet carved at a dial (its heights as generated become the carved ones, as
 * `withLandforms` does), keeping the world's key (`carvedGroundKey`: the spin axis, the wind and the sheet's seeded sites stay
 * the world's whatever the dial, and the world is not taken for a landform world). Off, or an airless world: the planet unchanged.
 */
export function withErosion(planet: PlanetState, dial: ErosionDial | undefined): PlanetState {
  const graph = planetRouteGraph(planet);
  const c = erosionCut(planet, graph, graph.radii, dial, undefined, worldDrainageOf(planet));
  if (!c) return planet;
  const heightmap = carvedHeights(planet, graph, c.delta);
  return { ...planet, heightmap, initialHeightmap: heightmap, carvedGroundKey: groundKey(planet) };
}

/**
 * 74.19's yards (DECISIONS 438): the planet a match's setup plans on without the landforms — unchanged, the dial off's — with
 * the carved heights kept aside (`carved`) for the setup's last step to lay, its yards held (core/carveYards.ts layCarve).
 * Off, an airless world or nothing carved: the planet unchanged.
 */
export function plannedErosion(planet: PlanetState, dial: ErosionDial | undefined): PlanetState {
  const graph = planetRouteGraph(planet);
  const c = erosionCut(planet, graph, graph.radii, dial, undefined, worldDrainageOf(planet));
  if (!c) return planet;
  return { ...planet, carved: { heightmap: carvedHeights(planet, graph, c.delta), shapings: planet.shapings ?? [], ...(planet.screeFacets ? { screeFacets: planet.screeFacets } : {}), levelVerts: [] } };
}

/** A carve's change laid on the planet's generated heights, one per mesh corner, clamped as the generator clamps. */
export function carvedHeights(planet: PlanetState, graph: PlanetRouteGraph, delta: Float64Array): Float32Array {
  const R = planet.radius, maxUp = MAX_TERRAIN_HEIGHT_FRACTION * R, maxDown = -MAX_CRATER_DEPTH_FRACTION * R;
  const h0 = planet.initialHeightmap ?? planet.heightmap, out = new Float32Array(h0.length);
  for (let i = 0; i < h0.length; i++) { const dv = delta[graph.nodeOf[i]]; out[i] = dv === 0 ? h0[i] : Math.max(maxDown, Math.min(maxUp, h0[i] + dv)); }
  return out;
}
